import React, { useEffect, useState } from 'react';
import * as wasm from 'sched';
import { WorkspaceContext } from '../../context/WorkspaceContext';
import { useDatabase } from '../../hooks/useDatabase';
import TypeBadge from '../common/TypeBadge';

// Contexts the list can operate in
export type DependencyContext = { kind: 'Task' | 'Outcome'; id: string };
export type ActorContext = { kind: 'Person' | 'Team'; id: string };

type Props =
  | { type: 'object'; object: wasm.NodeMatcher }
  | { type: 'dependency'; dependency: DependencyContext; actor?: never }
  | { type: 'actor'; dependency?: never; actor: ActorContext };

const WorkMatchList: React.FC<Props> = (props) => {
  const { safeData } = React.useContext(WorkspaceContext);
  const { database, applyCommand } = useDatabase();
  const [rows, setRows] = useState<wasm.WorkMatch[] | null>(null);

  // Helpers to format labels from bindings safely without any-casts
  const formatObject = (m: wasm.WorkMatch): { label: string; type: 'Task' | 'Outcome' | 'Project' | 'Tag' | 'Item' } => {
    const obj = m.object as unknown as Record<string, unknown>;
    if ('TaskId' in obj && obj.TaskId && typeof (obj.TaskId as any).id === 'string') {
      const id = (obj.TaskId as any).id as string;
      const t = database?.tasks()?.[id];
      return { label: t ? t.title() : `Task ${id}`, type: 'Task' };
    }
    if ('OutcomeId' in obj && obj.OutcomeId && typeof (obj.OutcomeId as any).id === 'string') {
      const id = (obj.OutcomeId as any).id as string;
      const o = database?.outcomes()?.[id];
      return { label: o ? o.title() : `Outcome ${id}`, type: 'Outcome' };
    }
    if ('ProjectId' in obj && obj.ProjectId && typeof (obj.ProjectId as any).id === 'string') {
      const id = (obj.ProjectId as any).id as string;
      const p = database?.projects()?.[id];
      return { label: p ? p.name() : `Project ${id}`, type: 'Project' };
    }
    if ('Tag' in obj && obj.Tag && typeof (obj.Tag as any).tag === 'string') {
      const tag = (obj.Tag as any).tag as string;
      return { label: `#${tag}`, type: 'Tag' };
    }
    if (obj === 'AllWork' || 'AllWork' in obj) {
      return { label: 'All work', type: 'Item' };
    }
    return { label: 'Item', type: 'Item' };
  };

  const formatActor = (m: wasm.WorkMatch): { label: string; type: 'Person' | 'Team' } => {
    const actor = m.actor as unknown as Record<string, unknown>;
    if ('Person' in actor && typeof actor.Person === 'string') {
      const id = actor.Person as string;
      const p = database?.people()?.[id];
      return { label: p ? p.name() : `Person ${id}`, type: 'Person' };
    }
    if ('Team' in actor && typeof actor.Team === 'string') {
      const id = actor.Team as string;
      const t = database?.teams?.()?.[id];
      return { label: t ? t.name() : `Team ${id}`, type: 'Team' };
    }
    return { label: 'Actor', type: 'Person' };
  };

  const load = async () => {
    if (!safeData || !database) return;
    switch (props.type) {
      case 'dependency': {
        const dep: wasm.NodeMatcher = props.dependency.kind === 'Task'
          ? { TaskId: { id: props.dependency.id } }
          : { OutcomeId: { id: props.dependency.id } };
        const list = await wasm.get_relevant_matches_for_object(safeData, dep);
        setRows(list ?? []);
        break;
      }

      case 'actor': {
        const actor: wasm.PersonOrTeam = props.actor.kind === 'Person'
          ? { Person: props.actor.id }
          : { Team: props.actor.id };
        const list = await wasm.get_relevant_matches_for_person_or_team(safeData, actor);
        setRows(list ?? []);
        break;
      }

      case 'object': {
        const obj: wasm.NodeMatcher = props.object;
        const list = await wasm.get_relevant_matches_for_object(safeData, obj);
        setRows(list ?? []);
        break;
      }
    };
  }

  useEffect(() => {
    load();
    // Refresh when database instance changes or the id in context changes
    // TODO update this
  }, [database, safeData, 'dependency' in props ? props.dependency?.id : undefined, 'actor' in props ? props.actor?.id : undefined]);

  const onRemove = async (m: wasm.WorkMatch) => {
    const command: wasm.DatabaseCommand = {
      type: 'SetWorkMatch',
      work_match: m,
      enabled: false,
    };
    await applyCommand(command);
    await load();
  };

  return (
    <div className="mb-4">
      <div className="font-medium mb-2">Existing Matches</div>
      <div className="rounded-md border border-cm-border-subtle overflow-hidden">
        <div className="grid grid-cols-[1fr_1fr_auto] gap-3 px-3 py-2 bg-cm-bg-elevated text-xs text-cm-text-secondary">
          <div>What</div>
          <div>Who</div>
          <div className="text-right pr-1">Actions</div>
        </div>
        <div className="divide-y divide-cm-border-subtle overflow-y-auto max-h-96 sm:max-h-[40vh] overscroll-contain">
          {rows?.length === 0 && (
            <div className="p-3 text-center text-cm-text-muted text-sm bg-cm-bg-surface">No matches are configured</div>
          )}
          {rows?.map((m, idx) => {
            const obj = formatObject(m);
            const act = formatActor(m);
            return (
              <div key={idx} className="grid grid-cols-[1fr_1fr_auto] gap-3 items-center px-3 py-2 bg-cm-bg-surface text-cm-text-primary">
                <div className="flex items-center gap-2 min-w-0">
                  <TypeBadge t={obj.type} />
                  <span className="truncate">{obj.label}</span>
                </div>
                <div className="flex items-center gap-2 min-w-0">
                  <TypeBadge t={act.type} />
                  <span className="truncate">{act.label}</span>
                </div>
                <div className="flex justify-end">
                  <button
                    type="button"
                    className="inline-flex items-center px-2 py-1 text-xs rounded text-cm-text-muted hover:text-red-400 hover:bg-cm-bg-hover transition-colors"
                    onClick={() => onRemove(m)}
                    title="Remove"
                    aria-label="Remove match"
                  >
                    ✕
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default WorkMatchList;
